"use client";
import Thumbnail from "@marginly/ui/components/thumbnail";
import T from "@marginly/ui/components/typography";
import cn from "classnames";
import { useState } from "react";

import { useWeb3Context } from "@/contexts/web3";
import { sortByTotal } from "@/util/sort-by-total";

import { formatPercent } from "../../util/formatters";
import {
  PoolCardsContainer,
  PoolTitleLeft,
  PoolWrapper,
  PoolWrapperTitle,
  ShowRestCard,
  ThumbnailWrapper,
} from "../common/accordion";
import { Noop } from "../util";
import { useAprMap } from "../util/use-apr-map";
import { usePoolsStateMap } from "../util/use-pools-state-map";
import { Card } from "./card";
import { Pool, Section } from "./types";
import { getAdditionalPoolInfo } from "./utils/get-additional-pool-Info";

const MIN_APR = 0.00001;

export const PoolSection = ({
  chain,
  id,
  title,
  isOpen,
  pools,
  priceByToken,
}: {
  isOpen?: true;
  chain: string;
  id: Section["id"];
  pools: Pool[];
  title: string;
  priceByToken?: Record<`0x${string}`, number>;
}) => {
  const poolIds = pools.map((pool) => pool.address).filter(Boolean);

  const { getPoolByAddress } = useWeb3Context();
  const poolsApr = useAprMap(poolIds);
  const poolsTotal = usePoolsStateMap(poolIds);

  const poolsInfo = pools
    .map((pool) => {
      const additionalPoolInfo = getAdditionalPoolInfo({
        pool,
        poolInfo: getPoolByAddress(pool.address),
        poolsApr,
        poolsTotal,
        priceByToken,
      });
      if (!additionalPoolInfo) return null;
      return { ...pool, ...additionalPoolInfo };
    })
    .filter((pool) => !!pool);

  const [isPoolDetails, setIsPoolDetails] = useState(Boolean(isOpen));
  const [isFullList, setFullListStatus] = useState(false);

  const maxApr = Math.max(...poolsInfo.map(({ apr }) => apr), 0);

  const handleShowRestCards = () => {
    setFullListStatus(true);
  };

  const poolsWithAprSorted = sortByTotal(poolsInfo);

  const poolsWithLargeApr = poolsInfo.filter(({ apr }) => apr > MIN_APR);
  const showAllPools = poolsInfo.length === poolsWithLargeApr.length;

  const renderThumbnail = () => {
    if (id === "ether") {
      return <img src="/images/eth-v.svg" alt="" />;
    }
    if (id === "stablecoins") {
      if (chain === "blast") {
        return <img src="/images/usdb.svg" alt="" />;
      }
      return <img src="/images/usdc-b.svg" alt="" />;
    }
    if (id === "other") {
      if (chain === "blast") {
        return <img src="/images/blast-yellow-circle.svg" alt="" />;
      }
      return (
        <img
          src="/images/thumbnail-group.png"
          alt=""
          style={{ height: "48px", objectFit: "contain" }}
        />
      );
    }

    return (
      <Thumbnail md accent className="">
        <img
          src={
            id === "pendle"
              ? "/images/token/pendle.svg"
              : id === "rewards"
              ? "/images/gift-b.svg"
              : ""
          }
          alt=""
        />
      </Thumbnail>
    );
  };
  return (
    <PoolWrapper
      className={cn({
        colored: id === "pendle" || id === "rewards",
      })}
    >
      <PoolWrapperTitle onClick={() => setIsPoolDetails(!isPoolDetails)}>
        <PoolTitleLeft>
          <ThumbnailWrapper>
            {renderThumbnail()}
            {id === "pendle" || id === "rewards" ? (
              <img src="/images/greenspark.svg" alt="" className="sparkling" />
            ) : (
              <Noop />
            )}
          </ThumbnailWrapper>
          <div>
            <T headerS>{title}</T>
            <T caption secondary>
              Up to{" "}
              {formatPercent(maxApr, {
                maximumFractionDigits: 1,
              })}{" "}
              APY
            </T>
          </div>
        </PoolTitleLeft>

        <img
          src="/images/arrow-down.svg"
          alt=""
          style={{
            transform: isPoolDetails ? "rotate(180deg)" : "",
            transition: "200ms ease-out",
            filter: "invert(var(--svg-invert))",
          }}
        />
      </PoolWrapperTitle>
      {isPoolDetails && poolsWithAprSorted?.length ? (
        <PoolCardsContainer>
          {poolsWithAprSorted?.map((pool, key) => {
            const {
              address,
              apr,
              isBaseToken,
              bonus,
              totalCollateral,
              totalDebt,
            } = pool;
            if (!isFullList && apr < MIN_APR) {
              return null;
            }
            return (
              <Card
                totalCollateral={totalCollateral}
                totalDebt={totalDebt}
                chain={chain}
                isBaseToken={isBaseToken}
                poolAddress={address}
                apr={apr}
                key={key}
                bonus={bonus}
              />
            );
          })}

          {!isFullList && !showAllPools && (
            <ShowRestCard onClick={handleShowRestCards}>
              <T headerS secondary>
                Show the rest assets
              </T>
            </ShowRestCard>
          )}
        </PoolCardsContainer>
      ) : (
        <Noop />
      )}
    </PoolWrapper>
  );
};
